// Toaster — non-blocking feedback for actions that succeed.
//
// Errors belong inline, next to what failed (ErrorState, or the dialog that
// raised them) — a toast is the wrong place for something the operator must
// act on, because it disappears. Toasts are for confirmations: "Refund
// issued", "Card removed", "Copied".
//
// Deliberately tiny and dependency-free: a context, a reducer, and a
// fixed-position list with an aria-live region.

import { cn } from '@fajrarabi/ui';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Icon, type IconName } from './Icon';

export type ToastTone = 'success' | 'info' | 'warning' | 'error';

export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  /** Optional follow-up, e.g. "Undo" or "View invoice". */
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toast: (message: string, options?: { tone?: ToastTone; action?: Toast['action'] }) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE: Record<ToastTone, { icon: IconName; className: string }> = {
  success: { icon: 'checkCircle', className: 'text-success-700 bg-success-50 ring-success-500/20' },
  info: { icon: 'inbox', className: 'text-brand-700 bg-brand-50 ring-brand-500/20' },
  warning: { icon: 'alert', className: 'text-warning-700 bg-warning-50 ring-warning-500/20' },
  error: { icon: 'alert', className: 'text-error-700 bg-error-50 ring-error-500/20' },
};

const AUTO_DISMISS_MS = 4500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback<ToastContextValue['toast']>(
    (message, options) => {
      const id = nextId.current++;
      setToasts((list) => [
        ...list,
        { id, message, tone: options?.tone ?? 'success', action: options?.action },
      ]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS),
      );
    },
    [dismiss],
  );

  // Clear every pending timer on unmount, or a dismiss fires against a
  // torn-down component.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        // polite, not assertive: a success confirmation must not interrupt a
        // screen-reader user mid-sentence.
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed bottom-4 end-4 z-[60] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2.5 rounded-xl px-3.5 py-3 shadow-lg ring-1 ring-inset',
              TONE[t.tone].className,
            )}
          >
            <Icon name={TONE[t.tone].icon} className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="min-w-0 flex-1 text-sm font-medium">{t.message}</p>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action?.onClick();
                  dismiss(t.id);
                }}
                className="shrink-0 rounded px-1 text-sm font-semibold underline underline-offset-2"
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="shrink-0 rounded p-0.5 opacity-60 transition-opacity hover:opacity-100"
            >
              <Icon name="close" className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
